import { useEffect, useReducer, useState } from 'react';
import { CameraImageTimelineResponse } from '../types';

// Action types for the image preloader reducer
type ImageAction =
  | { type: 'RESET' }
  | {
      type: 'ADD_IMAGE';
      uri: string;
      base64: string;
      imageElement: HTMLImageElement;
    }
  | { type: 'ADD_URI'; uri: string; imageElement: HTMLImageElement } // Fallback for CORS issues
  | { type: 'LOADING_ERROR'; uri: string };

// State for managing preloaded images
interface PreloadedImageState {
  htmlElements: Record<string, HTMLImageElement>;
  base64Images: Record<string, string>;
}

// Reducer for managing preloaded images to avoid race conditions
const imageReducer = (state: PreloadedImageState, action: ImageAction): PreloadedImageState => {
  switch (action.type) {
    case 'RESET':
      return { htmlElements: {}, base64Images: {} };
    case 'ADD_IMAGE':
      return {
        htmlElements: {
          ...state.htmlElements,
          [action.uri]: action.imageElement,
        },
        base64Images: {
          ...state.base64Images,
          [action.uri]: action.base64,
        },
      };
    case 'ADD_URI':
      return {
        htmlElements: {
          ...state.htmlElements,
          [action.uri]: action.imageElement,
        },
        base64Images: {
          ...state.base64Images,
          [action.uri]: action.uri, // Store URI as fallback
        },
      };
    case 'LOADING_ERROR':
      // Keep current state, don't add failed image
      return state;
    default:
      return state;
  }
};

// Helper function to convert image to base64
const imageToDataURL = (img: HTMLImageElement): string => {
  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext('2d');
  ctx?.drawImage(img, 0, 0);
  return canvas.toDataURL('image/jpeg');
};

interface UseImagePreloaderOptions {
  cameraTimeline?: CameraImageTimelineResponse | null;
  authToken?: string;
  pogImageUri?: string; // Additional POG image to preload
}

interface UseImagePreloaderReturn {
  preloadedImages: Record<string, HTMLImageElement>;
  preloadedImagesB64: Record<string, string>;
  isLoadingFirstImage: boolean;
  isLoading: boolean;
}

export const useImagePreloader = ({
  cameraTimeline,
  authToken,
  pogImageUri,
}: UseImagePreloaderOptions): UseImagePreloaderReturn => {
  const [imageState, dispatchImages] = useReducer(imageReducer, {
    htmlElements: {},
    base64Images: {},
  });
  const [isLoadingFirstImage, setIsLoadingFirstImage] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Preload all timeline images and convert to base64
  useEffect(() => {
    if (!cameraTimeline?.images || cameraTimeline.images.length === 0) {
      dispatchImages({ type: 'RESET' });
      return;
    }

    // Reset images for new timeline
    dispatchImages({ type: 'RESET' });

    let loadedCount = 0;
    let totalCount = 0;
    let cancelled = false;
    setIsLoadingFirstImage(true);
    setIsLoading(true);

    const checkFirstImageLoaded = () => {
      if (!cancelled && loadedCount === 1) {
        setIsLoadingFirstImage(false);
      }
    };

    const checkAllImagesLoaded = () => {
      if (!cancelled && loadedCount === totalCount) {
        setIsLoading(false);
      }
    };

    // Collect all images to preload (timeline images + POG images)
    const allImages = [...cameraTimeline.images];

    // Collect POG images
    const pogImages: typeof allImages = [];

    // Add POG images from timeline
    if (cameraTimeline.pogs) {
      Object.values(cameraTimeline.pogs).forEach((pog) => {
        if (pog && !allImages.find((img) => img.imageUri === pog.pogImageUri)) {
          pogImages.push({
            imageId: 0,
            imageUri: pog.pogImageUri,
            detectedAt: '',
            templateId: 0,
            ilSectionIds: [],
          });
        }
      });
    }

    // Add additional POG image if provided
    if (pogImageUri && !pogImages.find((img) => img.imageUri === pogImageUri)) {
      pogImages.push({
        imageId: 0,
        imageUri: pogImageUri,
        detectedAt: '',
        templateId: 0,
        ilSectionIds: [],
      });
    }

    // Priority images: first timeline image + POG images
    const firstImage = allImages.length > 0 ? allImages[0] : null;
    const priorityImages = firstImage ? [firstImage, ...pogImages] : pogImages;

    totalCount = allImages.length + pogImages.length;

    // Remaining images (excluding the first one since it's in priority)
    const remainingImages = firstImage ? allImages.slice(1) : allImages;

    // Helper function to load a single image
    const loadImage = (imageItem: (typeof allImages)[0]) => {
      return new Promise<void>((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous'; // Enable cross-origin for canvas conversion

        img.onload = () => {
          if (cancelled) {
            resolve();
            return;
          }

          try {
            const base64 = imageToDataURL(img);
            loadedCount++;
            dispatchImages({
              type: 'ADD_IMAGE',
              uri: imageItem.imageUri,
              base64,
              imageElement: img,
            });
            checkFirstImageLoaded();
            checkAllImagesLoaded();
          } catch (error) {
            console.warn(
              'Failed to convert image to base64 (CORS issue), using URI fallback:',
              error
            );
            loadedCount++;
            dispatchImages({
              type: 'ADD_URI',
              uri: imageItem.imageUri,
              imageElement: img,
            });
            checkFirstImageLoaded();
            checkAllImagesLoaded();
          }
          resolve();
        };

        img.onerror = () => {
          if (cancelled) {
            resolve();
            return;
          }

          loadedCount++;
          dispatchImages({
            type: 'LOADING_ERROR',
            uri: imageItem.imageUri,
          });
          checkFirstImageLoaded();
          checkAllImagesLoaded();
          resolve();
        };

        const imageUrl = authToken
          ? `${imageItem.imageUri}?token=${authToken}`
          : imageItem.imageUri;
        img.src = imageUrl;
      });
    };

    // Load priority images first, then remaining images
    (async () => {
      // Load all priority images
      await Promise.all(priorityImages.map((imageItem) => loadImage(imageItem)));

      // After priority images are loaded and delay, load remaining images
      if (!cancelled && remainingImages.length > 0) {
        remainingImages.forEach((imageItem) => loadImage(imageItem));
      }
    })();

    return () => {
      // Cancel any pending operations
      cancelled = true;
    };
  }, [cameraTimeline, authToken, pogImageUri]);

  return {
    preloadedImages: imageState.htmlElements,
    preloadedImagesB64: imageState.base64Images,
    isLoadingFirstImage,
    isLoading,
  };
};
